import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import Select from '../../../components/ui/Select';

const ProductCatalogUpload = ({ onUpload, onClose }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [category, setCategory] = useState('');
  const [bulkImport, setBulkImport] = useState(false);

  const categoryOptions = [
    { value: 'financial-services', label: 'Financial Services' },
    { value: 'insurance', label: 'Insurance Products' },
    { value: 'investment', label: 'Investment Plans' },
    { value: 'loans', label: 'Loan Products' },
    { value: 'credit-cards', label: 'Credit Cards' },
    { value: 'savings', label: 'Savings Accounts' }
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      setUploadedFile(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      setUploadedFile(e?.target?.files?.[0]);
    }
  };

  const handleUpload = () => {
    if (uploadedFile && category) {
      onUpload({ file: uploadedFile, category, bulkImport });
      setUploadedFile(null);
      setCategory('');
      setBulkImport(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1300] flex items-start justify-center pt-[8vh] px-4">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-card rounded-2xl shadow-elevation-5 p-6 md:p-8 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">Upload Product Catalog</h2>
            <p className="text-sm text-muted-foreground mt-1">Import products with bulk validation and category mapping</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="space-y-6">
          <Select
            label="Product Category"
            description="Select the category for your products"
            required
            options={categoryOptions}
            value={category}
            onChange={setCategory}
            placeholder="Choose a category"
          />

          <div
            className={`border-2 border-dashed rounded-xl p-8 md:p-12 text-center transition-smooth ${
              dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Upload" size={32} color="var(--color-primary)" className="md:w-10 md:h-10" />
              </div>
              
              {uploadedFile ? (
                <div className="flex items-center gap-3 px-4 py-3 bg-muted rounded-lg">
                  <Icon name="FileText" size={20} color="var(--color-success)" />
                  <span className="text-sm font-medium text-foreground">{uploadedFile?.name}</span>
                  <button
                    onClick={() => setUploadedFile(null)}
                    className="p-1 rounded hover:bg-background transition-smooth"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <p className="text-base md:text-lg font-medium text-foreground mb-2">
                      Drag and drop your file here
                    </p>
                    <p className="text-sm text-muted-foreground">
                      or click to browse from your computer
                    </p>
                  </div>
                  
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="file-upload">
                    <Button variant="outline" size="default" asChild>
                      <span className="cursor-pointer">Browse Files</span>
                    </Button>
                  </label>
                  
                  <p className="text-xs text-muted-foreground">
                    Supported formats: CSV, XLSX, XLS (Max 10MB)
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
            <input
              type="checkbox"
              id="bulk-import"
              checked={bulkImport}
              onChange={(e) => setBulkImport(e?.target?.checked)}
              className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
            />
            <label htmlFor="bulk-import" className="flex-1 cursor-pointer">
              <p className="text-sm font-medium text-foreground">Enable Bulk Import Validation</p>
              <p className="text-xs text-muted-foreground mt-1">
                Automatically validate product data, check for duplicates, and map categories during import
              </p>
            </label>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
              <div className="text-xs text-muted-foreground space-y-1">
                <p>• Ensure your file includes columns: Product Name, SKU, Price, Description, Category</p>
                <p>• Maximum 1000 products per upload</p>
                <p>• Duplicate SKUs will be flagged for review</p>
                <p>• Invalid data will be highlighted in the validation report</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button variant="outline" fullWidth onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="default" 
              fullWidth 
              disabled={!uploadedFile || !category}
              onClick={handleUpload}
            >
              Upload Catalog
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCatalogUpload;